/** Part C — Broker Commission (Scheduled Commission) focused modal (percent-first Feb 2026). */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { tokenStorage } from '../../../lib/tokenStorage';
import { emitDealUpdate } from '../../../hooks/useDealUpdates';
import CommissionEntryFields from './CommissionEntryFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${tokenStorage.getToken()}` });

export default function BrokerCommissionResolutionModal({ open, onOpenChange, deal, onResolved }) {
  const funding = deal?.funding_amount;
  const initialAmount = deal?.broker_commission ?? '';
  const initialPct = (funding && Number.isFinite(parseFloat(initialAmount)))
    ? ((parseFloat(initialAmount) / funding) * 100).toString()
    : '';
  const [state, setState] = useState({ pct: initialPct, amount: initialAmount === null ? '' : String(initialAmount) });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const amt = deal?.broker_commission ?? '';
    const pct = (funding && amt !== '' && amt != null)
      ? ((parseFloat(amt) / funding) * 100).toString()
      : '';
    setState({ pct, amount: amt === null || amt === undefined ? '' : String(amt) });
  }, [open, deal, funding]);

  const handleSave = async () => {
    if (!deal?.id) return;
    setSubmitting(true);
    try {
      const parsed = state.amount === '' ? null : parseFloat(state.amount);
      await axios.patch(`${API}/deals/${deal.id}`, { broker_commission: parsed }, { headers: authHeaders() });
      toast.success('Scheduled Commission saved');
      emitDealUpdate({ dealId: deal.id, source: 'resolve-broker_commission' });
      if (onResolved) onResolved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Scheduled Commission');
    } finally {
      setSubmitting(false);
    }
  };

  const canSave = !submitting && (state.amount === '' || !isNaN(parseFloat(state.amount)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="resolve-broker-commission-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Scheduled Commission
          </DialogTitle>
          <DialogDescription>
            The commission this deal owes the broker/ISO. Enter as a percentage of funding or as a dollar amount.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <CommissionEntryFields
            funding_amount={funding}
            value={state}
            onChange={setState}
            testidPrefix="resolve-broker-commission"
            pctLabel="Percentage of funding"
            amountLabel="Scheduled amount ($)"
            disabled={submitting}
          />
          <p className="mt-3 text-xs text-muted-foreground">Leave blank to clear; the warning will re-appear.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="resolve-broker-commission-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="resolve-broker-commission-confirm-btn">
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
